// Framework-agnostic helpers shared across the persolog frontends.
// Anything here must stay free of app coupling (no app stores, keycloak, API
// clients or i18n instance) — only Vue/vue-router/date-fns primitives.
import { nextTick } from "vue";
import { format, isValid, lightFormat, parseISO, parse } from "date-fns";
import type { RouteLocationNormalizedLoaded, Router } from "vue-router";

// ---- third-party embeds ----
export { HUBSPOT_SCRIPT_ID, hubSpotScriptUrl, loadHubSpot } from "./hubspot";

// ---- domain vocabularies shared by the apps ----
export { ParticipantActivityType } from "./participant-activity";

// ---- sorting ----
export { compareIdentifiers } from "./identifier-sort";

// ---- notification banners ----
export { useNotificationBanners } from "./notification-banners";

// ---- navigation / urls ----
export const openInNewTab = (url: string) => {
    globalThis.open(url, "_blank", "noopener,noreferrer");
};

export const openInSameTab = (url: string) => {
    globalThis.open(url, "_self", "noopener,noreferrer");
};

export const openInNewTabSafariSafe = (url: string) => {
    if (detectBrowser() === "Safari") {
        openInSameTab(url);
    } else {
        openInNewTab(url);
    }
};

export function normalizeWebsiteUrl(value?: string | null): string {
    const trimmedValue = value?.trim() ?? "";
    if (trimmedValue.length === 0) {
        return "";
    }
    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmedValue)) {
        return trimmedValue;
    }
    return `https://${trimmedValue}`;
}

/**
 * Returns a safe redirect URL with only whitelisted query params (projectId, participantId, chartId).
 */
export function getSafeRedirect(urlString: string): string {
    const url = new URL(urlString);
    const safeParams = new URLSearchParams();
    const projectId = url.searchParams.get("projectId");
    if (projectId !== null) {
        safeParams.set("projectId", projectId);
    }
    const participantId = url.searchParams.get("participantId");
    if (participantId !== null) {
        safeParams.set("participantId", participantId);
    }
    // Group-chart email links deep-link to a specific chart via chartId; keep it across login.
    const chartId = url.searchParams.get("chartId");
    if (chartId !== null) {
        safeParams.set("chartId", chartId);
    }
    return url.origin + url.pathname + (safeParams.toString() ? "?" + safeParams.toString() : "");
}

export function detectBrowser() {
    const userAgent = navigator.userAgent;
    const browsers = ["Chrome", "Firefox", "Safari", "Edge"];
    return browsers.find((browser) => userAgent.includes(browser)) ?? "Other";
}

// ---- DOM / focus ----
export const scrollToID = (id: string, pixelOffset: number = 0) => {
    const element = globalThis.document.getElementById(id);
    if (element === null) return;
    if (pixelOffset != 0) element.style.scrollMargin = pixelOffset + "px";
    element.setAttribute("tabindex", "0");
    element.dataset.dynamicTab = "true";
    element.focus({ preventScroll: true });
    element.scrollIntoView({ behavior: "smooth" });
};

export function bringFocusToElementById(id: string) {
    const el = document.getElementById(id);
    if (el) el.focus();
}

export function blurFocusByElementId(id: string) {
    const el = document.getElementById(id);
    if (el) el.blur();
}

/**
 * Focus the first focusable element inside a dialog, given its ID.
 */
export function focusFirstElementInDialog(dialogId: string) {
    nextTick(() => {
        const container = document.getElementById(dialogId);
        if (container === null) return;
        const focusable = container.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable instanceof HTMLElement) focusable.focus();
    });
}

export const autofocusFix = (event: Event) => {
    const menu = event.target as HTMLElement;
    nextTick(() => {
        const activeItemID = menu.getAttribute("aria-activedescendant");
        if (activeItemID) {
            menu.removeAttribute("aria-activedescendant");
            const activeItem = menu.querySelector("#" + activeItemID) as HTMLElement;
            if (activeItem) {
                activeItem.dataset.pFocused = "false";
                activeItem.classList.remove("p-focus");
            }
        }
    });
};

export const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
};

// ---- view ----
/**
 * Checks if the current view is on a mobile screen (default breakpoint 768px).
 */
export function isMobileView(mobileViewWidth?: number): boolean {
    if (mobileViewWidth === undefined || mobileViewWidth === 0) {
        return globalThis.innerWidth <= 768;
    }
    return globalThis.innerWidth <= mobileViewWidth;
}

// ---- i18n display / time zone (no app i18n instance needed) ----
export const getDisplayName = (locale: string, lang: string) => {
    const baseLang = lang.split("_")[0];
    const region = lang.split("_")[1];
    // Locale codes may arrive in the Java form ("zh_CN", possibly uppercased
    // by callers) — Intl requires a BCP-47 tag and throws on underscores.
    const displayLocale = locale.split("_")[0].toLowerCase();
    try {
        const displayName = new Intl.DisplayNames([displayLocale], { type: "language" }).of(baseLang);
        if (region) {
            let regionName = new Intl.DisplayNames([displayLocale], { type: "region" }).of(region);
            if (region === "CN") regionName = "Simplified";
            if (region === "TW") regionName = "Traditional";
            return `${displayName} (${regionName})`;
        }
        return displayName ?? lang;
    } catch {
        return lang;
    }
};

export function getCurrentTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// ---- dates ----
/**
 * Parses a backend date-time string as UTC. The backend stores plain LocalDateTime meaning UTC
 * wall-clock and emits it both as "2026-07-21T22:00:00" (JSON) and as "2026-07-21 22:00"
 * (DB string columns), so zone-less strings get an explicit "Z"; strings that already carry a
 * zone or offset (e.g. produced by toISOString()) are parsed as-is.
 */
export function parseServerDate(value: string): Date {
    const normalized = value.replace(" ", "T");
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized)) return parseISO(normalized);
    return parseISO(normalized.includes("T") ? normalized + "Z" : normalized + "T00:00:00Z");
}

/**
 * Reads the calendar day (UTC) of a backend date-time string as a local Date at midnight — the
 * shape date-only DatePickers expect. Counterpart of toServerDateOnly/toServerEndOfDay: values
 * written by those helpers round-trip to the same calendar day in every timezone.
 */
export function parseServerDateOnly(value: string): Date {
    const instant = parseServerDate(value);
    return new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
}

/**
 * Serializes a date-only picker value as UTC midnight of the picked calendar day, so the stored
 * day is timezone-independent and reopening + saving without changes never shifts it.
 */
export function toServerDateOnly(date: Date): string {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
}

/** Like toServerDateOnly but at the last millisecond of the picked day (expiry-style deadlines). */
export function toServerEndOfDay(date: Date): string {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)).toISOString();
}

/**
 * Parses a backend UTC date string (with or without explicit zone) and formats it in the
 * viewer's local timezone. Defaults to "dd.MM.yyyy HH:mm".
 */
export const formatDate = (date: string, dateFormat: string = "dd.MM.yyyy HH:mm") => {
    return format(parseServerDate(date), dateFormat);
};

/**
 * Serializes a local Date to the backend's UTC query-param format
 * ("yyyy-MM-dd HH:mm:ss"). Backend timestamps are stored as UTC wall-clock, so
 * range filters must be converted from local time, not formatted verbatim.
 */
export const toBackendDateTime = (date: Date): string => date.toISOString().slice(0, 19).replace("T", " ");

export function parseDate(dateStr: string, formatStr: string): Date {
    return parse(dateStr, formatStr, new Date());
}

/**
 * True when `now` falls inside the optional [start, end] display window, both bounds inclusive.
 * Bounds are ISO strings interpreted in the viewer's local timezone (display windows are
 * user-facing, so the browser clock decides); a date-only end ("2026-08-31") covers its whole
 * calendar day. Omitted or unparsable bounds leave that side open, so a typo can never hide
 * permanent content.
 */
export function isWithinDisplayWindow(start?: string, end?: string, now: Date = new Date()): boolean {
    if (start) {
        const from = parseISO(start);
        if (isValid(from) && now < from) return false;
    }
    if (end) {
        const until = parseISO(end);
        if (!end.includes("T")) until.setHours(23, 59, 59, 999);
        if (isValid(until) && now > until) return false;
    }
    return true;
}

/**
 * Who a banner is meant for. Mirrors the `BannerAudience` of the language-management api and the
 * `audience` of the start-page galleria's own banner list — one vocabulary for both kinds of banner.
 */
export type BannerAudience = "DE" | "INT" | "ALL";

/** Minimal shape of a viewer, so this does not depend on any app's full Trainer type. */
export interface BannerViewer {
    distributor: { id: number };
}

/**
 * The audience a viewer belongs to: a logged-in trainer by their distributor (1 is the German one),
 * an anonymous visitor by their interface language. Extracted from PersologGalleria so the
 * start-page banners and the notification banners cannot drift apart (RDT-339).
 */
export function resolveBannerAudience(locale: string, trainer?: BannerViewer | null): BannerAudience {
    if (trainer) {
        return trainer.distributor.id === 1 ? "DE" : "INT";
    }
    return locale === "de" ? "DE" : "INT";
}

/**
 * One notification banner as the api serves it. The active flag and the display window are
 * evaluated server-side, so anything that reaches the client is due to be shown — only the
 * audience is decided here, because only the client knows the viewer's distributor.
 */
export interface NotificationBannerItem {
    id: number;
    text: string;
    audience: BannerAudience;
}

/**
 * One start-page galleria banner as the api serves it (RDT-359). Like the notification banners, the
 * active flag and the display window are already applied server-side, so anything that arrives here
 * is due to be shown — only the audience is decided in the browser, because only the browser knows
 * the viewer's distributor.
 *
 * The two srcs are ready to render: the app's api layer has already prefixed the path the backend
 * returns with that app's own base, so the browser sees a same-origin url (see the `/banner-images/`
 * proxy in each app's nginx.conf).
 */
export interface StartPageBannerItem {
    id: number;
    desktopSrc: string;
    mobileSrc: string;
    alt: string;
    link?: string | null;
    audience: BannerAudience;
}

/**
 * Turns the service-relative image path the api returns ("/banner-images/<hash>.webp") into a url on
 * the app's own origin. `base` is the app's base path — "/" for profile-administration, and
 * "/profile-download/" for the download frontend, which is served under a sub-path and therefore
 * proxies the images under that same prefix.
 */
export function bannerImageUrl(path: string, base: string): string {
    return `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

/** True when a banner addressed to `audience` should be shown to a viewer in `viewerAudience`. */
export function matchesBannerAudience(audience: BannerAudience, viewerAudience: BannerAudience): boolean {
    return audience === "ALL" || audience === viewerAudience;
}

/**
 * First day of the current month → today.
 */
export const initializeDates = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return [firstDayOfMonth, today];
};

/**
 * Expands a [start, end] range to cover the full days.
 */
export const adjustDateTimes = (dates: Array<Date>) => {
    const adjustedDateTimes = dates;
    if (adjustedDateTimes?.length === 2) {
        if (adjustedDateTimes[0]) adjustedDateTimes[0].setHours(0, 0, 0, 0);
        if (adjustedDateTimes[1]) adjustedDateTimes[1].setHours(23, 59, 59, 999);
    }
    return adjustedDateTimes;
};

export const filenameFromTimeRange = (
    name: string | undefined,
    dateStart: string | undefined,
    dateEnd: string | undefined,
) => {
    const filenameBase = name ?? "download";
    const formattedStart = dateStart && isValid(parseISO(dateStart)) ? parseISO(dateStart) : null;
    const formattedEnd = dateEnd && isValid(parseISO(dateEnd)) ? parseISO(dateEnd) : null;
    const parts = [filenameBase];
    if (formattedStart) parts.push(lightFormat(formattedStart, "yyyy-MM-dd"));
    if (formattedEnd && formattedStart?.getTime() !== formattedEnd.getTime()) {
        parts.push(lightFormat(formattedEnd, "yyyy-MM-dd"));
    }
    return parts.join("_");
};

// ---- ISO-8601 month periods ("P6M") ----
export const parseMonthPeriod = (period?: string) => {
    if (period === undefined || period === "") return null;
    const match = new RegExp(/P(\d+)M/).exec(period);
    return match ? Number.parseInt(match[1], 10) : null;
};

export const formatMonthPeriod = (number: number) => {
    return `P${number}M`;
};

// ---- misc ----
export function refreshPage() {
    location.reload();
}

const concurrencyLimit = 15;
type ProcessFunction<T, R> = (item: T) => Promise<R>;

/**
 * Runs an async function over a list in fixed-size concurrent batches.
 */
export async function processInBatches<T, R>(items: T[], processFunction: ProcessFunction<T, R>): Promise<R[]> {
    let results: R[] = [];
    for (let i = 0; i < items.length; i += concurrencyLimit) {
        const batch = items.slice(i, i + concurrencyLimit);
        const batchResults = await Promise.all(batch.map(processFunction));
        results = results.concat(batchResults);
    }
    return results;
}

// ---- router query params ----
export function setQueryParameter(
    route: RouteLocationNormalizedLoaded,
    router: Router,
    queryParameter: string,
    value: string | number,
) {
    const currentQuery = { ...route.query };
    currentQuery[queryParameter] = value.toString();
    router.replace({ query: currentQuery });
}

/**
 * Removes one or more query params in a SINGLE navigation. Always pass every param you want gone in
 * one call: chaining removals (this or removeQueryParameter, back-to-back) races, because each call
 * reads the not-yet-updated route.query and the later replace() re-adds params an earlier one removed.
 */
export function removeQueryParameters(
    route: RouteLocationNormalizedLoaded,
    router: Router,
    queryParameters: string[],
) {
    const currentQuery = { ...route.query };
    for (const queryParameter of queryParameters) {
        delete currentQuery[queryParameter];
    }
    router.replace({ query: currentQuery });
}

/** Convenience wrapper around removeQueryParameters for the common single-param case. */
export function removeQueryParameter(route: RouteLocationNormalizedLoaded, router: Router, queryParameter: string) {
    removeQueryParameters(route, router, [queryParameter]);
}

export function getQueryParameter(route: RouteLocationNormalizedLoaded, queryParameter: string): string | undefined {
    return route.query[queryParameter] as string | undefined;
}

export function ensurePdfExtension(filename: string): string {
    return filename.toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`;
}

/**
 * Resolves the download filename for a profile PDF response. The backend sends the
 * canonical "{productNo}-{firstName}-{lastName}-{language}.pdf" name URL-encoded in the
 * X-Filename header; older backends send it raw, so decoding falls back to the raw value.
 * Without a header the caller's fallback (usually "{productNo}-{participantId}-{language}") is used.
 */
export function extractPdfFilenameFromHeaders(
    headers: Record<string, string | undefined>,
    fallback: string,
): string {
    const normalizedHeaders = Object.fromEntries(
        Object.entries(headers).map(([key, value]) => [key.toLowerCase(), value]),
    );

    const explicitFilenameHeader = normalizedHeaders["x-filename"] || normalizedHeaders.filename;
    if (explicitFilenameHeader?.trim()) {
        const trimmed = explicitFilenameHeader.trim();
        const withoutLeadingQuote = trimmed.startsWith('"') ? trimmed.slice(1) : trimmed;
        const cleaned = withoutLeadingQuote.endsWith('"') ? withoutLeadingQuote.slice(0, -1) : withoutLeadingQuote;
        return ensurePdfExtension(decodeFilenameHeader(cleaned));
    }

    return ensurePdfExtension(fallback);
}

function decodeFilenameHeader(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}
